/**
 * Badger in the room (docs/features/badger-cohost.md, Phase 2 contract, Watch screen): he is on a stage while a remote
 * participant of kind AGENT whose `stage_id` attribute names it is in the room. Asked for (room metadata's `cohost`) and
 * present are independent, so this reads only the room. His state is LiveKit's `lk.agent.state` attribute, and his
 * audio track's muted state is the truth for the mic glyph and the console's Mute.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react'
import { ParticipantEvent, ParticipantKind, RemoteAudioTrack, RoomEvent, Track, type Participant, type Room, type TrackPublication } from 'livekit-client'

/** The attribute naming the stage an agent serves (set by `services/cohost` as it joins). */
export const COHOST_STAGE_ATTRIBUTE = 'stage_id'
/** The attribute naming which co-host an agent is, and Badger's value of it (set beside `stage_id`). */
export const COHOST_ATTRIBUTE = 'cohost'
export const COHOST_PERSONA = 'badger'
/** The attribute LiveKit Agents keeps current with the agent's state. */
export const AGENT_STATE_ATTRIBUTE = 'lk.agent.state'

/** What LiveKit Agents says an agent is doing (`lk.agent.state`). */
export const COHOST_STATES = ['initializing', 'idle', 'listening', 'thinking', 'speaking'] as const
export type CohostState = (typeof COHOST_STATES)[number]

/** The agent's state from its attribute; null for none or a value this page doesn't know. */
export function cohostState(raw: string | null | undefined): CohostState | null {
  return (COHOST_STATES as readonly string[]).includes(raw ?? '') ? (raw as CohostState) : null
}

/** This participant is the stage's Badger: an agent (only our worker, holding our API secret, can join as one) for it. */
export function isCohostOf(participant: Participant, stageId: string): boolean {
  return participant.kind === ParticipantKind.AGENT && participant.attributes?.[COHOST_STAGE_ATTRIBUTE] === stageId
}

/** The stage's Badger among the room's remote participants, or null while he isn't in it. */
export function findCohost(room: Pick<Room, 'remoteParticipants'>, stageId: string): Participant | null {
  for (const participant of room.remoteParticipants.values()) {
    if (isCohostOf(participant, stageId)) return participant
  }
  return null
}

/** His voice: the microphone track he publishes (any audio track, should a worker publish under another source). */
export function cohostAudio(participant: Participant): TrackPublication | null {
  return participant.getTrackPublication(Track.Source.Microphone) ?? participant.audioTrackPublications?.values().next().value ?? null
}

/** His audio track is muted (the console's Mute, through the agent). No track published is not muted: he is silent. */
export function cohostMuted(participant: Participant): boolean {
  return cohostAudio(participant)?.isMuted ?? false
}

/** What the page follows of Badger once he is in the room. */
export interface CohostLive {
  /** `lk.agent.state`; null before LiveKit says, or for a state this page doesn't know. */
  state: CohostState | null
  /** His voice reaches the audience: LiveKit hears him, or the agent says it is speaking, and he isn't muted. */
  speaking: boolean
  /** His audio track is muted. */
  muted: boolean
  /** His audio track while this tab receives it, for the mouth's level; null otherwise. */
  track: RemoteAudioTrack | null
}

export const NO_COHOST_LIVE: CohostLive = { state: null, speaking: false, muted: false, track: null }

export function readCohost(participant: Participant): CohostLive {
  const state = cohostState(participant.attributes?.[AGENT_STATE_ATTRIBUTE])
  const audio = cohostAudio(participant)
  const muted = audio?.isMuted ?? false
  const track = audio?.track instanceof RemoteAudioTrack ? audio.track : null
  return { state, speaking: !muted && (participant.isSpeaking || state === 'speaking'), muted, track }
}

const sameLive = (a: CohostLive, b: CohostLive) => a.state === b.state && a.speaking === b.speaking && a.muted === b.muted && a.track === b.track

/** The room's arrivals, departures and attribute changes (an agent sets `stage_id` just after it joins), and the connection. */
const ROOM_EVENTS = [
  RoomEvent.ParticipantConnected,
  RoomEvent.ParticipantDisconnected,
  RoomEvent.ParticipantAttributesChanged,
  RoomEvent.ConnectionStateChanged,
] as const

/** What changes his state, voice or track. */
const LIVE_EVENTS = [
  ParticipantEvent.AttributesChanged,
  ParticipantEvent.IsSpeakingChanged,
  ParticipantEvent.TrackPublished,
  ParticipantEvent.TrackUnpublished,
  ParticipantEvent.TrackSubscribed,
  ParticipantEvent.TrackUnsubscribed,
  ParticipantEvent.TrackMuted,
  ParticipantEvent.TrackUnmuted,
] as const

/** What changes whether his track is muted. */
const MUTE_EVENTS = [ParticipantEvent.TrackPublished, ParticipantEvent.TrackUnpublished, ParticipantEvent.TrackMuted, ParticipantEvent.TrackUnmuted] as const

type Listen = (event: string, listener: () => void) => unknown

/** Subscribes `onChange` to each of `events` on `emitter`, returning the unsubscribe. */
function listen(emitter: { on: unknown; off: unknown }, events: readonly string[], onChange: () => void) {
  for (const event of events) (emitter.on as Listen).call(emitter, event, onChange)
  return () => {
    for (const event of events) (emitter.off as Listen).call(emitter, event, onChange)
  }
}

/**
 * `read()` as it is now, re-rendering when `subscribe`'s events change what it reads (by `Object.is`) and not otherwise.
 * What `useSyncExternalStore` does, without its React 18 gap: in a render that also schedules a render-phase update (a
 * `setState` during render, as the watch screen's hooks do), it loses the value it read, and later misses a change back
 * to it. Here the value last shown is kept after every commit.
 */
function useFollowed<T>(subscribe: (onChange: () => void) => () => void, read: () => T): T {
  const value = read()
  const shown = useRef(value)
  useLayoutEffect(() => {
    shown.current = value
  })
  const [, rerender] = useReducer((count: number) => count + 1, 0)
  useEffect(() => {
    const check = () => {
      if (!Object.is(read(), shown.current)) rerender()
    }
    const unsubscribe = subscribe(check)
    // Anything that changed between the render and subscribing.
    check()
    return unsubscribe
  }, [subscribe, read])
  return value
}

/**
 * The stage's Badger in the room, or null: re-renders only when that changes (he arrives, leaves, or an agent's
 * `stage_id` comes to name the stage), not with his speaking or state. Null without a stage.
 */
export function useCohostParticipant(room: Room, stageId: string | null): Participant | null {
  const subscribe = useCallback((onChange: () => void) => listen(room, ROOM_EVENTS, onChange), [room])
  const read = useCallback(() => (stageId ? findCohost(room, stageId) : null), [room, stageId])
  return useFollowed(subscribe, read)
}

/** The stage's Badger's identity while he is in the room (for the voices the page plays), else null. */
export function useCohostIdentity(room: Room, stageId: string | null): string | null {
  return useCohostParticipant(room, stageId)?.identity ?? null
}

/** Badger's state, voice and track as LiveKit says them, following his participant; nothing without one. */
export function useCohostLive(participant: Participant | null): CohostLive {
  const store = useMemo(() => {
    let last = NO_COHOST_LIVE
    return {
      subscribe: (onChange: () => void) => (participant ? listen(participant, LIVE_EVENTS, onChange) : () => {}),
      read: () => {
        const next = participant ? readCohost(participant) : NO_COHOST_LIVE
        if (!sameLive(last, next)) last = next
        return last
      },
    }
  }, [participant])
  return useFollowed(store.subscribe, store.read)
}

/** Whether his audio track is muted, re-rendering only when that changes (not with his speaking or state). */
export function useCohostMuted(participant: Participant | null): boolean {
  const subscribe = useCallback((onChange: () => void) => (participant ? listen(participant, MUTE_EVENTS, onChange) : () => {}), [participant])
  const read = useCallback(() => (participant ? cohostMuted(participant) : false), [participant])
  return useFollowed(subscribe, read)
}

export interface CohostPresence extends CohostLive {
  /** His participant while he is on the stage, else null. */
  participant: Participant | null
  identity: string | null
}

/** The stage's Badger: whether he is in the room and, while he is, his state, voice and mute. */
export function useCohost(room: Room, stageId: string | null): CohostPresence {
  const participant = useCohostParticipant(room, stageId)
  const live = useCohostLive(participant)
  return useMemo(() => ({ participant, identity: participant?.identity ?? null, ...live }), [participant, live])
}
